import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface SiteOption {
  id: string;
  name: string;
  primary_domain: string | null;
  location_served: string;
  status: string;
  tagline: string | null;
  ownership_type: string | null;
}

interface GlobalAdminContextType {
  isGlobalAdmin: boolean;
  loading: boolean;
  checked: boolean; // True only after admin check has completed
  sites: SiteOption[];
  selectedSiteId: string | null;
  setSelectedSiteId: (siteId: string) => void;
  selectedSite: SiteOption | null;
  refetchSites: () => Promise<void>;
}

const GlobalAdminContext = createContext<GlobalAdminContextType | undefined>(undefined);

export function GlobalAdminProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(false);
  const [sites, setSites] = useState<SiteOption[]>([]);
  const [selectedSiteId, setSelectedSiteIdState] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setChecked(false); // Reset checked when user changes
      checkGlobalAdmin();
    } else {
      setIsGlobalAdmin(false);
      setSites([]);
      setSelectedSiteIdState(null);
      setLoading(false);
      setChecked(true); // Mark as checked (no user = not admin)
    }
  }, [user]);

  const fetchSites = async (isGlobal: boolean) => {
    if (isGlobal) {
      const { data: allSites } = await supabase
        .from('sites')
        .select('id, name, primary_domain, location_served, status, tagline, ownership_type')
        .order('name');

      if (allSites) {
        setSites(allSites);
        
        // Restore selected site from localStorage or default to first
        const stored = localStorage.getItem('globalAdmin_selectedSiteId');
        if (stored && allSites.some(s => s.id === stored)) {
          setSelectedSiteIdState(stored);
        } else if (allSites.length > 0 && !selectedSiteId) {
          setSelectedSiteIdState(allSites[0].id);
        }
      }
    } else {
      // For regular site admins, get their site
      const { data: siteAdmin } = await supabase
        .from('site_admins')
        .select('site_id, sites(id, name, primary_domain, location_served, status, tagline, ownership_type)')
        .eq('user_id', user!.id)
        .maybeSingle();

      if (siteAdmin?.sites) {
        const site = siteAdmin.sites as unknown as SiteOption;
        setSites([site]);
        setSelectedSiteIdState(site.id);
      }
    }
  };

  const checkGlobalAdmin = async () => {
    try {
      // Check if user is a global admin
      const { data: globalAdmin } = await supabase
        .from('global_admins')
        .select('id')
        .eq('user_id', user!.id)
        .maybeSingle();

      const isGlobal = !!globalAdmin;
      setIsGlobalAdmin(isGlobal);
      await fetchSites(isGlobal);
    } catch (error) {
      console.error('Error checking global admin status:', error);
    } finally {
      setLoading(false);
      setChecked(true); // Mark as checked AFTER everything completes
    }
  };

  const refetchSites = async () => {
    await fetchSites(isGlobalAdmin);
  };

  const setSelectedSiteId = (siteId: string) => {
    setSelectedSiteIdState(siteId);
    localStorage.setItem('globalAdmin_selectedSiteId', siteId);
  };

  const selectedSite = sites.find(s => s.id === selectedSiteId) || null;

  return (
    <GlobalAdminContext.Provider 
      value={{ 
        isGlobalAdmin, 
        loading,
        checked,
        sites, 
        selectedSiteId, 
        setSelectedSiteId,
        selectedSite,
        refetchSites
      }}
    >
      {children}
    </GlobalAdminContext.Provider>
  );
}

export function useGlobalAdmin() {
  const context = useContext(GlobalAdminContext);
  if (context === undefined) {
    throw new Error('useGlobalAdmin must be used within a GlobalAdminProvider');
  }
  return context;
}
